import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Accordion } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchAllMarketPlaces } from "../../../store/actions/marketplace.action";
import { publishMarketplaceListingBulk } from "../../../store/actions/marketplaceListing.action";
import EbayStep from "./EbayStep";
import "./AddToMarketplaceModal.css";

const STATUS = {
    PENDING: "pending",
    UPLOADING: "uploading",
    PUBLISHED: "published",
    DRAFTED: "drafted",
    FAILED: "failed",
    SKIPPED: "skipped",
};

// Soft pastel chip palette — tuned for low visual weight so status is legible
// without competing with the form fields inside each accordion row.
const STATUS_STYLE = {
    [STATUS.PENDING]: { text: "Pending", bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8" },
    [STATUS.UPLOADING]: { text: "Uploading", bg: "#e0f2fe", fg: "#0369a1", dot: "#38bdf8" },
    [STATUS.PUBLISHED]: { text: "Published", bg: "#dcfce7", fg: "#166534", dot: "#22c55e" },
    [STATUS.DRAFTED]: { text: "Drafted", bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
    [STATUS.FAILED]: { text: "Failed", bg: "#fee2e2", fg: "#991b1b", dot: "#ef4444" },
    [STATUS.SKIPPED]: { text: "Skipped", bg: "#f1f5f9", fg: "#64748b", dot: "#cbd5e1" },
};

const StatusChip = ({ status }) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE[STATUS.PENDING];
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: s.bg,
                color: s.fg,
                fontSize: 12,
                fontWeight: 500,
                padding: "2px 10px",
                borderRadius: 999,
                lineHeight: 1.6,
                whiteSpace: "nowrap",
            }}
        >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: s.dot }} />
            {s.text}
        </span>
    );
};

StatusChip.propTypes = { status: PropTypes.string.isRequired };

// Fields eligible to live in sharedPayload; if all items agree on a value we
// hoist it to shared, otherwise each divergent item carries it as an override.
const SHAREABLE_FIELDS = [
    "categoryId",
    "condition",
    "currency",
    "merchantLocationKey",
    "fulfillmentPolicyId",
    "paymentPolicyId",
    "returnPolicyId",
];

// Fields that are always per-item (never hoisted to sharedPayload).
const ALWAYS_OVERRIDE_FIELDS = [
    "sku", "title", "description",
    "price", "quantity",
    "imageUrls",
    "brand", "mpn", "upc", "ean",
    "aspects",
];

const extractItemError = (result) => {
    if (!result) return "No response returned for this product.";
    const raw = result.lastError || result.error?.message || result.error || result.message;
    if (!raw) return "Publish failed.";
    // Some backend errors arrive as a JSON string wrapping the raw eBay response.
    // Parse it and surface the matching per-SKU error message so the banner is readable.
    if (typeof raw === "string" && raw.startsWith("{")) {
        try {
            const parsed = JSON.parse(raw);
            const responses = parsed?.responses;
            if (Array.isArray(responses) && responses.length) {
                const mine = responses.find((r) => r.sku === result.sku) || responses[0];
                const err = mine?.errors?.[0];
                if (err?.message) return err.message;
            }
        } catch (_) {
            // Fall through and return the raw string below.
        }
    }
    return String(raw);
};

const isMeaningful = (v) => {
    if (v === undefined || v === null || v === "") return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return true;
};

const pickMeaningful = (payload, keys) => {
    const out = {};
    for (const k of keys) {
        if (isMeaningful(payload[k])) out[k] = payload[k];
    }
    return out;
};

/**
 * From an array of per-item flat payloads, compute the subset of SHAREABLE_FIELDS
 * whose value is identical (and meaningful) across every item.
 */
const computeSharedFields = (flatPayloads) => {
    const shared = {};
    for (const field of SHAREABLE_FIELDS) {
        const first = flatPayloads[0]?.[field];
        if (!isMeaningful(first)) continue;
        const allSame = flatPayloads.every((p) => p[field] === first);
        if (allSame) shared[field] = first;
    }
    return shared;
};

/**
 * Assemble the sharedPayload envelope the /publish-bulk endpoint expects. Note
 * that merchantLocationKey is renamed to sharedLocationKey and policy fields
 * are grouped under sharedPolicies to match the API contract.
 */
const buildSharedPayload = ({ marketplaceRegion, shared }) => {
    const sharedPayload = { marketplaceRegion };
    if (shared.condition) sharedPayload.condition = shared.condition;
    if (shared.currency) sharedPayload.currency = shared.currency;
    if (shared.categoryId) sharedPayload.categoryId = shared.categoryId;
    if (shared.merchantLocationKey) sharedPayload.sharedLocationKey = shared.merchantLocationKey;

    const sharedPolicies = {};
    if (shared.fulfillmentPolicyId) sharedPolicies.fulfillmentPolicyId = shared.fulfillmentPolicyId;
    if (shared.paymentPolicyId) sharedPolicies.paymentPolicyId = shared.paymentPolicyId;
    if (shared.returnPolicyId) sharedPolicies.returnPolicyId = shared.returnPolicyId;
    if (Object.keys(sharedPolicies).length > 0) sharedPayload.sharedPolicies = sharedPolicies;

    return sharedPayload;
};

/** Build the `overrides` block for one item — always-per-item fields plus any shareable field not in shared. */
const buildOverrides = (flatPayload, shared) => {
    const overrides = pickMeaningful(flatPayload, ALWAYS_OVERRIDE_FIELDS);
    for (const field of SHAREABLE_FIELDS) {
        if (!(field in shared) && isMeaningful(flatPayload[field])) {
            overrides[field] = flatPayload[field];
        }
    }
    return overrides;
};

/**
 * BulkEbayListingModal
 *
 * Step-2 of the bulk "Add to Marketplace" flow. Renders an accordion with one
 * eBay listing form per selected product, and a single "Upload All to eBay"
 * button that POSTs one bulk request to /marketplace/ebay/listing/publish-bulk
 * carrying a `sharedPayload` (fields common to every product) and an `items[]`
 * array with per-product `overrides`.
 */
const BulkEbayListingModal = ({ show, onHide, products }) => {
    const dispatch = useDispatch();

    // Tbl_Marketplace lookup — stored as { value: id, label: name } (see
    // fetchAllMarketPlaces reducer). Used to resolve the numeric eBay
    // marketplace id EbayStep needs on the wire.
    const marketPlacesList = useSelector(
        (state) => state.InventoryManagement?.marketPlacesList
    ) || [];

    const [statusByProduct, setStatusByProduct] = useState({});
    const [errorByProduct, setErrorByProduct] = useState({});
    const [uploading, setUploading] = useState(false);

    // Map of productId -> child imperative handle (from EbayStep.forwardRef).
    const controllersRef = useRef({});
    const setControllerRef = (productId) => (node) => {
        if (node) controllersRef.current[productId] = node;
        else delete controllersRef.current[productId];
    };

    useEffect(() => {
        if (show && !marketPlacesList.length) {
            dispatch(fetchAllMarketPlaces());
        }
    }, [show, dispatch, marketPlacesList.length]);

    // Reset per-product status whenever the modal is re-opened with a new
    // selection so stale published/failed badges don't leak.
    useEffect(() => {
        if (show) {
            const initial = {};
            (products || []).forEach((p) => {
                initial[p.id ?? p.Id] = STATUS.PENDING;
            });
            setStatusByProduct(initial);
            setErrorByProduct({});
            setUploading(false);
        }
    }, [show, products]);

    const ebayMarketplaceId = useMemo(() => {
        const match = marketPlacesList.find(
            (m) => String(m.label || "").toLowerCase() === "ebay"
        );
        return match?.value || null;
    }, [marketPlacesList]);

    const setStatus = (productId, status) => {
        setStatusByProduct((prev) => ({ ...prev, [productId]: status }));
    };

    const handleDraftSaved = (productId) => {
        setStatus(productId, STATUS.DRAFTED);
    };

    const handleSkip = (productId) => {
        setStatus(productId, STATUS.SKIPPED);
    };

    const renderStatus = (productId) => {
        const status = statusByProduct[productId] || STATUS.PENDING;
        return <StatusChip status={status} />;
    };

    const productList = products || [];
    const defaultActiveKey = productList.length
        ? String(productList[0].id ?? productList[0].Id)
        : null;

    const statuses = Object.values(statusByProduct);
    const publishedCount = statuses.filter((s) => s === STATUS.PUBLISHED).length;
    const failedCount = statuses.filter((s) => s === STATUS.FAILED).length;

    /**
     * Validate that all target controllers exist and are ready for publishing.
     */
    const validateControllers = (targets) => {
        for (const { product, ctrl } of targets) {
            if (!ctrl) {
                toast.error(`${product.productSku || product.productName} is still initializing. Try again in a moment.`);
                return false;
            }
            if (!ctrl.isReady()) {
                toast.error(`${product.productSku || product.productName} isn't ready to publish — expand it to finish onboarding.`);
                return false;
            }
        }
        return true;
    };

    /**
     * Validate all target forms. Returns false on first validation error.
     */
    const validateAllForms = (targets) => {
        for (const { ctrl } of targets) {
            if (!ctrl.validate()) return false;
        }
        return true;
    };

    /**
     * Normalize response results into a map indexed by inventoryProductId.
     */
    const normalizeResultsMap = (body) => {
        const rawResults =
            body.data?.results
            || body.data?.items
            || (Array.isArray(body.data) ? body.data : []);
        const resultById = {};
        for (const r of (rawResults || [])) {
            const id = r.inventoryProductId ?? r.productId ?? r.id;
            if (id != null) resultById[id] = r;
        }
        return resultById;
    };

    /**
     * Process successful upload response and update states.
     */
    const processSuccessResponse = (targets, resultById) => {
        const finalStatus = {};
        const finalErrors = {};
        let successCount = 0;
        let failureCount = 0;

        targets.forEach(({ id, ctrl }) => {
            const r = resultById[id];
            if (r?.success) {
                finalStatus[id] = STATUS.PUBLISHED;
                ctrl?.applyBulkResult?.(r);
                successCount += 1;
            } else {
                finalStatus[id] = STATUS.FAILED;
                finalErrors[id] = extractItemError(r);
                failureCount += 1;
            }
        });

        setStatusByProduct((prev) => ({ ...prev, ...finalStatus }));
        setErrorByProduct((prev) => {
            const next = { ...prev };
            Object.keys(finalStatus).forEach((id) => {
                if (finalStatus[id] === STATUS.PUBLISHED) delete next[id];
            });
            return { ...next, ...finalErrors };
        });

        return { successCount, failureCount };
    };

    /**
     * Display appropriate success/failure toast based on result counts.
     */
    const displayResultToast = (successCount, failureCount) => {
        if (successCount && !failureCount) {
            toast.success(`Published ${successCount} listing${successCount === 1 ? "" : "s"} to eBay.`);
        } else if (successCount && failureCount) {
            toast.warn(`Published ${successCount}, failed ${failureCount}. Expand failed rows to see why.`);
        } else {
            toast.error("Bulk publish failed for all products.");
        }
    };

    /**
     * Handle upload failure by updating error states and displaying error toast.
     */
    const handleUploadFailure = (targets, errorMessage) => {
        toast.error(errorMessage);
        setStatusByProduct((prev) => {
            const next = { ...prev };
            targets.forEach(({ id }) => { next[id] = STATUS.FAILED; });
            return next;
        });
        setErrorByProduct((prev) => {
            const next = { ...prev };
            targets.forEach(({ id }) => { next[id] = errorMessage; });
            return next;
        });
    };

    /**
     * Build the complete bulk request payload from targets and shared fields.
     */
    const buildBulkRequestPayload = (targets) => {
        const flatPayloads = targets.map(({ ctrl }) => ctrl.buildFullPayload());
        const shared = computeSharedFields(flatPayloads);
        const marketplaceRegion = targets[0].ctrl.getMarketplaceRegion();
        const companySellerId = targets[0].ctrl.getCompanySellerId();
        const sharedPayload = buildSharedPayload({ marketplaceRegion, shared });

        const bulkItems = targets.map(({ id, ctrl }, idx) => {
            const flat = flatPayloads[idx];
            const overrides = buildOverrides(flat, shared);
            const item = { inventoryProductId: id, overrides };
            const existing = ctrl.getExistingListing?.();
            if (existing) item.existingListing = existing;
            return item;
        });

        return {
            companySellerId,
            marketplaceId: "ebay-us",
            sharedPayload,
            items: bulkItems,
        };
    };

    /**
     * Update initial uploading states for all targets.
     */
    const updateInitialUploadingState = (targets) => {
        setUploading(true);
        setStatusByProduct((prev) => {
            const next = { ...prev };
            targets.forEach(({ id }) => { next[id] = STATUS.UPLOADING; });
            return next;
        });
        setErrorByProduct((prev) => {
            const next = { ...prev };
            targets.forEach(({ id }) => { delete next[id]; });
            return next;
        });
    };

    /**
     * Fan out validation + payload collection to every child form, then POST
     * one bulk request. Per-item results are dispatched back to each child so
     * the success banner renders inside the accordion body.
     */
    const handleUploadAll = async () => {
        if (!productList.length || uploading) return;

        // Skip products already published so re-clicking the button after
        // fixing a failed row doesn't redundantly re-publish successes.
        const targets = productList
            .map((p) => {
                const id = p.id ?? p.Id;
                return { id, product: p, ctrl: controllersRef.current[id] };
            })
            .filter(({ id }) => statusByProduct[id] !== STATUS.PUBLISHED);

        if (!targets.length) {
            toast.info("All products are already published.");
            return;
        }

        if (!validateControllers(targets)) return;
        if (!validateAllForms(targets)) return;

        const requestBody = buildBulkRequestPayload(targets);
        updateInitialUploadingState(targets);

        try {
            const resp = await publishMarketplaceListingBulk("ebay", requestBody);
            const body = resp?.data;

            if (body?.success !== true) {
                const msg = body?.error?.message || "Bulk publish to eBay failed.";
                handleUploadFailure(targets, msg);
                return;
            }

            const resultById = normalizeResultsMap(body);
            const { successCount, failureCount } = processSuccessResponse(targets, resultById);
            displayResultToast(successCount, failureCount);
        } catch (err) {
            const msg = err?.response?.data?.error?.message || err.message || "Bulk publish to eBay failed.";
            handleUploadFailure(targets, msg);
        } finally {
            setUploading(false);
        }
    };

    // Extracted so the JSX map below stays legible. Renders one accordion row
    // whose header carries a persistent error strip (visible even when the
    // body is collapsed) instead of burying the error inside the form.
    const renderProductRow = (product) => {
        const productId = product.id ?? product.Id;
        const key = String(productId);
        const errorMessage = errorByProduct[productId];
        return (
            <Accordion.Item eventKey={key} key={key} className="mb-2" style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <Accordion.Header>
                    <div style={{ width: "100%" }}>
                        <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            {product.imageUrl ? (
                                <img
                                    src={product.imageUrl}
                                    alt=""
                                    style={{ height: 32, width: 32, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }}
                                />
                            ) : (
                                <span
                                    style={{
                                        display: "inline-block",
                                        height: 32,
                                        width: 32,
                                        background: "#f1f5f9",
                                        borderRadius: 6,
                                    }}
                                />
                            )}
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>
                                    {product.productSku || `#${productId}`}
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#6b7280",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {product.productName}
                                </div>
                            </div>
                            {renderStatus(productId)}
                        </div>
                    </div>
                </Accordion.Header>
                {errorMessage && (
                    <div
                        role="alert"
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            padding: "10px 16px",
                            background: "#fef2f2",
                            borderTop: "1px solid #fecaca",
                            color: "#991b1b",
                            fontSize: 13,
                            lineHeight: 1.5,
                        }}
                    >
                        <i className="fa fa-exclamation-circle" style={{ marginTop: 3, color: "#ef4444" }} aria-hidden="true" />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>Upload failed</div>
                            <div style={{ wordBreak: "break-word" }}>{errorMessage}</div>
                        </div>
                    </div>
                )}
                <Accordion.Body style={{ background: "#fbfbfd" }}>
                    <EbayStep
                        ref={setControllerRef(productId)}
                        product={product}
                        marketplaceId={ebayMarketplaceId}
                        hidePublishButton
                        onDraftSaved={() => handleDraftSaved(productId)}
                        onSkip={() => handleSkip(productId)}
                    />
                </Accordion.Body>
            </Accordion.Item>
        );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            aria-labelledby="bulk-ebay-listing-title"
            className="generateKey modalDeleteWrapper"
        >
            <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9" }}>
                <Modal.Title id="bulk-ebay-listing-title" style={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                    Add Products to eBay
                    {" "}
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 400,
                            color: "#9ca3af",
                            marginLeft: 10,
                        }}
                    >
                        {productList.length} product{productList.length === 1 ? "" : "s"}
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ background: "#fafbfc" }}>
                {productList.length === 0 && (
                    <p className="text-muted text-center my-4">No products selected.</p>
                )}

                {productList.length > 0 && (
                    <Accordion defaultActiveKey={defaultActiveKey} alwaysOpen={false} className="bulk-ebay-accordion">
                        {productList.map(renderProductRow)}
                    </Accordion>
                )}
            </Modal.Body>
            <Modal.Footer style={{ borderTop: "1px solid #f1f5f9" }}>
                <div className="w-100 d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: 13, color: "#6b7280", display: "inline-flex", gap: 16 }}>
                        {publishedCount > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 999, background: "#22c55e" }} />
                                {publishedCount} published
                            </span>
                        )}
                        {failedCount > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 999, background: "#ef4444" }} />
                                {failedCount} failed
                            </span>
                        )}
                    </span>
                    <div>
                        <Button className="cus-primary-transparent-btn mr-2" onClick={onHide} disabled={uploading}>
                            Close
                        </Button>
                        <Button
                            className="cus-seconday-bg-btn"
                            onClick={handleUploadAll}
                            disabled={uploading || productList.length === 0 || !ebayMarketplaceId}
                        >
                            {uploading ? "Uploading…" : `Upload All to eBay (${productList.length})`}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

BulkEbayListingModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    products: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            productSku: PropTypes.string,
            productName: PropTypes.string,
            imageUrl: PropTypes.string,
            companySellerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })
    ),
};

BulkEbayListingModal.defaultProps = {
    products: [],
};

export default BulkEbayListingModal;
